import { useMemo } from 'react';
import {
    Alert,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { useGetIdentity } from 'react-admin';

import type { ReceiptCategory, ResponsibleUser } from './receiptApi';
import { ProjectSearchAutocomplete } from './ProjectSearchAutocomplete';
import type { SimpleRecord } from './receiptHelpers';
import type { ReceiptFieldValues } from './bulkReceiptHelpers';

type Props = {
    value: ReceiptFieldValues;
    onChange: (next: ReceiptFieldValues) => void;
    categories: ReceiptCategory[];
    users: ResponsibleUser[];
    disabled?: boolean;
};

const suggestDescription = (merchant: string, category: string): string =>
    (merchant || category || '').substring(0, 20);

/** Controlled version of the single-upload form's fields, for one bulk item. */
export const ReceiptFieldsEditor = ({
    value,
    onChange,
    categories,
    users,
    disabled = false,
}: Props) => {
    const { identity } = useGetIdentity();
    const set = (patch: Partial<ReceiptFieldValues>) =>
        onChange({ ...value, ...patch });

    const selectedCategory = useMemo(
        () => categories.find(item => item.name === value.category),
        [categories, value.category]
    );

    return (
        <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <FormControl fullWidth required disabled={disabled}>
                    <InputLabel>Allocation type</InputLabel>
                    <Select
                        value={value.allocationType}
                        label="Allocation type"
                        onChange={event =>
                            set({
                                allocationType: event.target.value as
                                    | 'project_cost'
                                    | 'company_expense',
                                projectId: '',
                                projectNo: null,
                                category: '',
                                subcategory: '',
                            })
                        }
                    >
                        <MenuItem value="project_cost">Project cost (COGS)</MenuItem>
                        <MenuItem value="company_expense">Company expense</MenuItem>
                    </Select>
                </FormControl>
                {value.allocationType === 'project_cost' && (
                    <ProjectSearchAutocomplete
                        value={value.projectId}
                        onChange={projectId => set({ projectId })}
                        onProjectSelect={(project: SimpleRecord | null) =>
                            // Set projectId here too: onChange + onProjectSelect
                            // both merge onto the same stale `value`, so this
                            // second set must repeat projectId or it clobbers it.
                            set({
                                projectId: project ? String(project.id) : '',
                                projectNo: project?.project_no ?? null,
                            })
                        }
                        required
                        disabled={disabled}
                    />
                )}
                {value.allocationType === 'company_expense' && (
                    <FormControl fullWidth required disabled={disabled}>
                        <InputLabel>Expense category</InputLabel>
                        <Select
                            value={value.category}
                            label="Expense category"
                            onChange={event =>
                                set({ category: event.target.value, subcategory: '' })
                            }
                        >
                            {categories.map(item => (
                                <MenuItem key={item.name} value={item.name}>
                                    {item.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {value.allocationType === 'company_expense' &&
                    !!selectedCategory?.subcategories.length && (
                        <FormControl fullWidth required disabled={disabled}>
                            <InputLabel>Expense subcategory</InputLabel>
                            <Select
                                value={value.subcategory}
                                label="Expense subcategory"
                                onChange={event =>
                                    set({ subcategory: event.target.value })
                                }
                            >
                                {selectedCategory.subcategories.map(item => (
                                    <MenuItem key={item} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                    fullWidth
                    label="Merchant"
                    value={value.merchant}
                    disabled={disabled}
                    onChange={event => {
                        const merchant = event.target.value;
                        set({
                            merchant,
                            description: value.description
                                ? value.description
                                : suggestDescription(merchant, value.category),
                        });
                    }}
                />
                <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={value.amount}
                    disabled={disabled}
                    inputProps={{ min: 0, step: '0.01' }}
                    onChange={event => set({ amount: event.target.value })}
                />
                <TextField
                    fullWidth
                    label="Transaction date"
                    type="date"
                    value={value.transactionDate}
                    disabled={disabled}
                    InputLabelProps={{ shrink: true }}
                    onChange={event => set({ transactionDate: event.target.value })}
                />
                <FormControl fullWidth disabled={disabled}>
                    <InputLabel>Responsible employee</InputLabel>
                    <Select
                        value={value.responsibleUserId}
                        label="Responsible employee"
                        onChange={event =>
                            set({ responsibleUserId: event.target.value })
                        }
                    >
                        {identity?.id != null &&
                            !users.some(
                                user => String(user.id) === String(identity.id)
                            ) && <MenuItem value={String(identity.id)}>Me</MenuItem>}
                        {users.map(user => (
                            <MenuItem key={user.id} value={String(user.id)}>
                                {user.name || user.email}
                                {identity?.id != null &&
                                String(user.id) === String(identity.id)
                                    ? ' (me)'
                                    : ''}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>
            <TextField
                fullWidth
                label="Description (optional)"
                placeholder="Auto-suggested from merchant or category (max 20 chars)"
                value={value.description}
                disabled={disabled}
                onChange={event =>
                    set({ description: event.target.value.substring(0, 20) })
                }
                multiline
                rows={2}
                helperText={`${value.description.length}/20 characters`}
                variant="outlined"
            />
            {value.allocationType === 'project_cost' &&
                value.projectId === '' && (
                    <Alert severity="info">
                        Pick the project this receipt belongs to.
                    </Alert>
                )}
        </Stack>
    );
};
